/**
 * The AI-marking consent question, in one place.
 *
 * It is asked in three: on the sign-up form, on the account page, and in the
 * banner that catches accounts created before the question existed. Written
 * once here because it is the thing the learner is being held to — three
 * hand-maintained copies drift, and the day they disagree is the day nobody
 * can say what was actually agreed to.
 *
 * The wording is deliberately concrete about what leaves the server and what
 * declining costs, because "we use AI to improve your experience" tells a
 * sixteen-year-old nothing they could decide on.
 *
 * It names no vendor. More than one AI marker can be configured, and a
 * sentence naming one of them goes stale the moment that changes — which is
 * worse than saying less, because a learner who read it was told something
 * untrue. What leaves the server, and what does not, is the part that has to
 * be exact, and that is unaffected by which marker is in use.
 */

interface Props {
  /** Current answer. `null` renders with neither option selected. */
  value: boolean | null;
  onChange: (granted: boolean) => void;
  /** Radio group name — must be unique on a page showing more than one. */
  name?: string;
  disabled?: boolean;
}

/** What gets sent, and what happens if you say no. Shown above the choice. */
export function AiConsentExplainer() {
  return (
    <div className="consent-explainer">
      <p>
        PyDialog marks your written answers with an AI marker. When you finish
        a lesson, what you typed — your answer and your working — is sent to it
        to be read and marked.
      </p>
      <ul>
        <li>
          Your name, username, email and student number are <strong>not</strong>{" "}
          sent. Only the question and what you wrote.
        </li>
        <li>
          Say no and you can still study everything. Your work is marked on
          this server instead, without the detailed feedback the AI gives.
        </li>
        <li>You can change your mind at any time on your account page.</li>
      </ul>
    </div>
  );
}

export function AiConsentChoice({
  value,
  onChange,
  name = "ai-consent",
  disabled,
}: Props) {
  return (
    <div className="consent-choice" role="radiogroup" aria-label="AI marking">
      {/* Two explicit options rather than one tick-box. A single box that
          starts unticked reads as "agree to continue" and gets clicked
          without being read; making refusal a thing you actively choose is
          the difference between a decision and a formality. */}
      <label className={`consent-option${value === true ? " is-chosen" : ""}`}>
        <input
          type="radio"
          name={name}
          checked={value === true}
          onChange={() => onChange(true)}
          disabled={disabled}
        />
        <span>
          <strong>Yes, mark my answers with AI.</strong>
          <span className="consent-option-note">
            Full feedback: what you understood, what you missed, and where your
            reasoning went wrong.
          </span>
        </span>
      </label>
      <label className={`consent-option${value === false ? " is-chosen" : ""}`}>
        <input
          type="radio"
          name={name}
          checked={value === false}
          onChange={() => onChange(false)}
          disabled={disabled}
        />
        <span>
          <strong>No, keep my answers on this server.</strong>
          <span className="consent-option-note">
            Basic marking only. Nothing you write leaves PyDialog.
          </span>
        </span>
      </label>
    </div>
  );
}
